import { useState, useEffect } from "react";
import Header from "./components/Header";
import UserMenu from "./components/UserMenu";
import Button from "./components/Button";
import Card from "./components/Card";
import "./Explore.css";

const API = '/api';

export default function Explore({ user, onLogout, onGoHome, onOpenPen, onNewPen, onLogin, onEditor, onBlog, onExplore, onViewPens, onSettings }) {
  const [pens, setPens]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab]         = useState('trending');
  const [search, setSearch]   = useState('');
  const [likedPens, setLikedPens] = useState({});
  const [page, setPage]       = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  useEffect(() => {
    setLoading(true);
    setPage(1);
    fetch(`${API}/get_trending_pens.php?sort=${tab}&page=1`)
      .then(r => r.json())
      .then(data => {
        setPens(Array.isArray(data.pens) ? data.pens : []);
        setHasMore(!!data.has_more);
        setLoading(false);
      })
      .catch(() => { setPens([]); setHasMore(false); setLoading(false); });
  }, [tab]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    fetch(`${API}/get_trending_pens.php?sort=${tab}&page=${nextPage}`)
      .then(r => r.json())
      .then(data => {
        setPens(prev => [...prev, ...(Array.isArray(data.pens) ? data.pens : [])]);
        setHasMore(!!data.has_more);
        setPage(nextPage);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  };

  const filtered = pens.filter(p =>
    !search || (p.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.author_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenPen = (pen) => {
    // View count increment
    fetch(`${API}/view_pen.php?pen_id=${pen.id}`).catch(() => {});
    // Update views locally
    setPens(prev => prev.map(p => p.id === pen.id ? {...p, views: (parseInt(p.views)||0) + 1} : p));
    onOpenPen(pen);
  };

  const handleLike = async (e, pen) => {
    e.stopPropagation();
    if (!user) { alert('Like karne ke liye login karo!'); return; }
    const wasLiked = likedPens[pen.id];
    // Optimistic update
    setLikedPens(prev => ({...prev, [pen.id]: !wasLiked}));
    setPens(prev => prev.map(p => p.id === pen.id
      ? {...p, likes: (parseInt(p.likes)||0) + (wasLiked ? -1 : 1)}
      : p
    ));
    try {
      await fetch(`${API}/like_pen.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pen_id: pen.id, user_id: user.id })
      });
    } catch {
      // Revert on error
      setLikedPens(prev => ({...prev, [pen.id]: wasLiked}));
      setPens(prev => prev.map(p => p.id === pen.id
        ? {...p, likes: (parseInt(p.likes)||0) + (wasLiked ? 1 : -1)}
        : p
      ));
    }
  };

  return (
    <div className="explore-page">

      {/* NAV */}
      <Header
        onLogoClick={onGoHome}
        navItems={[
          { label: 'Editor',     onClick: onEditor },
          { label: 'Explore',    onClick: () => {} },
          { label: 'Challenges', onClick: () => {} },
          { label: 'Blog',       onClick: onBlog },
        ]}
        leftExtra={
          <div className="explore-tabs">
            <button
              className={`explore-tab ${tab === 'trending' ? 'active' : ''}`}
              onClick={() => setTab('trending')}
            >🔥 Trending</button>
            <button
              className={`explore-tab ${tab === 'latest' ? 'active' : ''}`}
              onClick={() => setTab('latest')}
            >✨ Latest</button>
          </div>
        }
        rightExtra={
          <div className="explore-search-box">
            <span className="explore-search-icon">🔍</span>
            <input
              className="explore-search"
              placeholder="Search projects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        }
        authSlot={
          user ? (
            <UserMenu
              user={user}
              onLogout={onLogout}
              items={[
                { label: 'New Pen',  icon: '✏️', onClick: onNewPen },
                { label: 'Home',     icon: '🏠', onClick: onGoHome },
                { label: 'My Pens',  icon: '📁', onClick: onViewPens },
                { label: 'Explore',  icon: '🔥', onClick: onExplore },
                { label: 'Settings', icon: '⚙️', onClick: onSettings },
              ]}
            />
          ) : (
            <Button variant="primary" size="sm" onClick={onLogin}>Login</Button>
          )
        }
      />

      {/* CONTENT */}
      <div className="explore-content">

        {/* Header */}
        <div className="explore-header">
          <div>
            <h1 className="explore-title">
              {tab === 'trending' ? '🔥 Trending Projects' : '✨ Latest Projects'}
            </h1>
            <p className="explore-subtitle">
              Discover what developers are building on WSNCode
            </p>
          </div>
          <Button variant="primary" onClick={onNewPen || onGoHome}>
            ✏️ New Project
          </Button>
        </div>

        {loading ? (
          <div className="explore-loading">
            <div className="explore-spinner"></div>
            <p>Loading projects...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="explore-empty">
            <div className="explore-empty-icon">🎨</div>
            <h2>No projects yet!</h2>
            <p>Be the first to share your code with the world.</p>
            <Button variant="primary" onClick={onNewPen || onGoHome}>
              ✏️ Create & Share a Pen
            </Button>
          </div>
        ) : (
          <div className="explore-grid">
            {filtered.map((pen) => (
              <Card
                key={pen.id}
                hoverable
                className="explore-card"
                onClick={() => handleOpenPen(pen)}
              >
                <div className="explore-card-preview">
                  <iframe
                    title={`explore-${pen.id}`}
                    sandbox="allow-scripts"
                    srcDoc={`<!DOCTYPE html><html><head><style>${pen.css||''}</style></head><body>${pen.html||''}<script>${pen.js||''}</script></body></html>`}
                    style={{
                      width: '200%',
                      height: '200%',
                      transform: 'scale(0.5)',
                      transformOrigin: 'top left',
                      pointerEvents: 'none',
                      border: 'none'
                    }}
                  />
                </div>
                <div className="explore-card-footer">
                  <div className="explore-card-avatar">
                    {getInitials(pen.author_name)}
                  </div>
                  <div className="explore-card-info">
                    <span className="explore-card-title">{pen.title || 'Untitled'}</span>
                    <span className="explore-card-author">by {pen.author_name || 'Anonymous'}</span>
                  </div>
                  <div className="explore-card-stats">
                    {/* Like button */}
                    <button
                      className={`explore-like-btn ${likedPens[pen.id] ? 'liked' : ''}`}
                      onClick={(e) => handleLike(e, pen)}
                      title={user ? 'Like' : 'Login to like'}
                    >
                      {likedPens[pen.id] ? '❤️' : '🤍'} {parseInt(pen.likes)||0}
                    </button>
                    {/* View count */}
                    <span className="explore-view-count">
                      👁️ {parseInt(pen.views)||0}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {!loading && !search && hasMore && (
          <div className="explore-load-more">
            <Button variant="secondary" onClick={handleLoadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}

      </div>
    </div>
  );
}